import React, { useState, useEffect } from "react";
import { T } from "../../../i18n/social";


// German public holidays (NRW)
function getGermanHolidays(year) {
  const a=year%19,b=Math.floor(year/100),k=Math.floor(b/4);
  const p=Math.floor((b+8)/25),q=Math.floor((b-p+1)/3);
  const M=(15+b-k-q)%30,N=(4+b-k)%7;
  const d=(19*a+M)%30,e=(2*k+4*N+6*d+N)%7;
  let ed=22+d+e,em=3;
  if(ed>31){ed-=31;em=4;}
  if(d===29&&e===6){ed=19;em=4;}
  if(d===28&&e===6&&a>10){ed=18;em=4;}
  const easter=new Date(year,em-1,ed);
  const add=(dt,days)=>{const x=new Date(dt);x.setDate(x.getDate()+days);return x;};
  const fmt=dt=>`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  return new Set([
    `${year}-01-01`,`${year}-05-01`,`${year}-10-03`,
    `${year}-11-01`,`${year}-12-25`,`${year}-12-26`,
    fmt(add(easter,-2)),fmt(easter),fmt(add(easter,1)),
    fmt(add(easter,39)),fmt(add(easter,49)),fmt(add(easter,50)),fmt(add(easter,60)),
  ]);
}
const NOW_YEAR = new Date().getFullYear();
const HOLIDAYS = new Set([...getGermanHolidays(NOW_YEAR), ...getGermanHolidays(NOW_YEAR + 1)]);


const WEEKDAY_LABELS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAY_LABELS_UK = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
const WEEKDAY_LABELS_RU = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTH_NAMES_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const MONTH_NAMES_UK = ["Січень","Лютий","Березень","Квітень","Травень","Червень","Липень","Серпень","Вересень","Жовтень","Листопад","Грудень"];
const MONTH_NAMES_RU = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
function getWeekdays(lang) { return lang === 'uk' ? WEEKDAY_LABELS_UK : lang === 'ru' ? WEEKDAY_LABELS_RU : WEEKDAY_LABELS_DE; }
function getMonths(lang) { return lang === 'uk' ? MONTH_NAMES_UK : lang === 'ru' ? MONTH_NAMES_RU : MONTH_NAMES_DE; }

function getBookingDates() {
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  const dates = [];
  let added = 0;
  let cursor = new Date(today);
  cursor?.setDate(cursor?.getDate() + 1);
  while (added < 14) {
    const dow = cursor?.getDay();
    if (dow !== 0 && dow !== 6) {
      dates?.push(new Date(cursor));
      added++;
    }
    cursor?.setDate(cursor?.getDate() + 1);
  }
  return dates;
}

function buildCalendarGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  let startDow = firstDay?.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  const daysInMonth = new Date(year, month + 1, 0)?.getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells?.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells?.push(new Date(year, month, d));
  return cells;
}

// Calendar skeleton loader
const CalendarSkeleton = () => (
  <div style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #dde1e7' }}>
    <div className="skeleton" style={{ height: '56px', borderRadius: 0 }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#f1f3f5', borderBottom: '1px solid #dde1e7', padding: '10px 0' }}>
      {[1,2,3,4,5,6,7]?.map(i => (
        <div key={i} style={{ display: 'flex', justifyContent: 'center' }}>
          <div className="skeleton" style={{ width: '20px', height: '12px', borderRadius: '4px' }} />
        </div>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#ffffff', padding: '8px', gap: '2px' }}>
      {Array.from({ length: 35 })?.map((_, i) => (
        <div key={i} style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {i % 3 !== 0 && <div className="skeleton" style={{ width: '32px', height: '32px', borderRadius: '50%', animationDelay: `${i * 30}ms` }} />}
        </div>
      ))}
    </div>
  </div>
);

const StepCalendar = ({ selectedDate, onSelect, consultationType, source, lang, stepBadge }) => {
  const tb = lang && T[lang]?.booking ? T[lang].booking : null;
  const [calReady, setCalReady] = React.useState(false);
  const [unavailableDates, setUnavailableDates] = React.useState(new Set());

  const bookingDates = getBookingDates();

  React.useEffect(() => {
    // Single call to /api/free-busy for the entire date range — 2 Graph API calls total
    const type = consultationType || 'onsite';
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const start = fmt(bookingDates[0]);
    const end = fmt(bookingDates[bookingDates.length - 1]);

    fetch(`/api/free-busy?startDate=${start}&endDate=${end}&type=${type}&source=${source || 'regular'}`)
      .then(r => r.json())
      .then(data => {
        const unavailable = new Set();
        for (const day of (data.days || [])) {
          // Day is unavailable if no slot exists where at least one berater is free
          const hasAnyFree = day.slots?.some(s => !s.conflict);
          if (!hasAnyFree) unavailable.add(day.date);
        }
        setUnavailableDates(unavailable);
        // Auto-jump to first month that has bookable dates
        const firstAvailable = bookingDates.find(date => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          return !unavailable.has(`${y}-${m}-${d}`);
        });
        if (firstAvailable) {
          setViewYear(firstAvailable.getFullYear());
          setViewMonth(firstAvailable.getMonth());
        }
        setCalReady(true);
      })
      .catch(() => setCalReady(true)); // on error just show calendar normally
  }, [consultationType]);
  const minDate = bookingDates?.[0];
  const maxDate = bookingDates?.[bookingDates?.length - 1];

  const [viewYear, setViewYear] = React.useState(minDate?.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(minDate?.getMonth());

  const cells = buildCalendarGrid(viewYear, viewMonth);

  const isBookable = (date) => {
    if (!date) return false;
    const dow = date?.getDay();
    if (dow === 0 || dow === 6) return false;
    const dateStr2 = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    if (HOLIDAYS.has(dateStr2)) return false;
    if (!bookingDates?.some((d) => d?.toDateString() === date?.toDateString())) return false;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return !unavailableDates.has(dateStr);
  };

  const isSelected = (date) =>
    date && selectedDate && date?.toDateString() === selectedDate?.toDateString();

  const isToday = (date) => {
    const today = new Date();
    return date && date?.toDateString() === today?.toDateString();
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const canPrev = new Date(viewYear, viewMonth, 1) > new Date(minDate?.getFullYear(), minDate?.getMonth(), 1);
  const canNext = new Date(viewYear, viewMonth, 1) < new Date(maxDate?.getFullYear(), maxDate?.getMonth(), 1);

  return (
    <div className="booking-card step-enter" style={{ maxWidth: '640px', width: '100%' }}>
      <div className="step-badge">{stepBadge || (lang === "uk" ? "Крок 3 з 6" : lang === "ru" ? "Шаг 3 из 6" : "Schritt 3 von 6")}</div>

      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', fontWeight: 700, color: '#1e2b38', marginBottom: '6px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
        {tb ? tb.calTitle : "Termin wählen"}
      </h2>
      <p style={{ fontSize: '14px', color: '#6b7a8d', marginBottom: '20px', fontFamily: 'var(--font-ui)' }}>
        {tb ? (consultationType === "onsite" ? tb.calOnsiteSub : tb.calPhoneSub) : (consultationType === "onsite" ? "Vor-Ort-Beratung · Kruppstraße 41-45, 42489 Wülfrath" : "Telefonberatung")} · {tb ? tb.calSub : "Wähle einen verfügbaren Tag"}
      </p>

      {!calReady ? (
        <CalendarSkeleton />
      ) : (
        <div style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #dde1e7' }}>
          {/* Calendar Header */}
          <div style={{ background: '#3E4954', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              type="button"
              onClick={prevMonth}
              disabled={!canPrev}
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', background: 'transparent', border: 'none', cursor: canPrev ? 'pointer' : 'not-allowed', opacity: canPrev ? 1 : 0.3, color: 'white', transition: 'background 150ms ease' }}
              onMouseEnter={e => { if (canPrev) e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <span style={{ color: 'white', fontWeight: 700, fontFamily: 'var(--font-ui)', fontSize: '15px', letterSpacing: '-0.01em' }}>
              {getMonths(lang)?.[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              disabled={!canNext}
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', background: 'transparent', border: 'none', cursor: canNext ? 'pointer' : 'not-allowed', opacity: canNext ? 1 : 0.3, color: 'white', transition: 'background 150ms ease' }}
              onMouseEnter={e => { if (canNext) e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>

          {/* Weekday headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#f1f3f5', borderBottom: '1px solid #dde1e7' }}>
            {getWeekdays(lang)?.map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: '11px', color: '#6b7a8d', padding: '10px 0', fontFamily: 'var(--font-ui)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#ffffff', padding: '8px', gap: '2px' }}>
            {cells?.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} />;
              const bookable = isBookable(date);
              const sel = isSelected(date);
              const tod = isToday(date);
              return (
                <button
                  key={date?.toISOString()}
                  type="button"
                  disabled={!bookable}
                  onClick={() => bookable && onSelect(date)}
                  style={{
                    aspectRatio: '1',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    fontSize: '13px',
                    fontFamily: 'var(--font-ui)',
                    fontWeight: sel ? 700 : bookable ? 600 : 400,
                    border: tod && !sel ? '2px solid #3E4954' : '2px solid transparent',
                    background: sel ? '#3E4954' : 'transparent',
                    color: sel ? '#ffffff' : bookable ? '#1e2b38' : 'rgba(30,43,56,0.25)',
                    cursor: bookable ? 'pointer' : 'not-allowed',
                    transition: 'all 150ms ease',
                    transform: sel ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: sel ? '0 4px 12px rgba(62,73,84,0.25)' : 'none',
                    outline: 'none',
                  }}
                  onMouseEnter={e => { if (bookable && !sel) { e.currentTarget.style.background = 'rgba(62,73,84,0.08)'; } }}
                  onMouseLeave={e => { if (!sel) { e.currentTarget.style.background = 'transparent'; } }}
                >
                  {date?.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p style={{ fontSize: '11px', color: '#6b7a8d', marginTop: '12px', textAlign: 'center', fontFamily: 'var(--font-ui)' }}>
        {lang === "uk" ? `Доступно: ${minDate?.toLocaleDateString("uk-UA")} – ${maxDate?.toLocaleDateString("uk-UA")} · Вихідні недоступні` : lang === "ru" ? `Доступно: ${minDate?.toLocaleDateString("ru-RU")} – ${maxDate?.toLocaleDateString("ru-RU")} · Выходные недоступны` : `Buchbar: ${minDate?.toLocaleDateString("de-DE")} – ${maxDate?.toLocaleDateString("de-DE")} · Wochenenden nicht verfügbar`}
      </p>
    </div>
  );
};

export default StepCalendar;